/**
 * PublicLandingPage Component
 * 
 * Landing page for unauthenticated users with pricing, features, and inline sign-in.
 * Includes Premium interest registration modal.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onSignIn - Callback when user signs in
 * @param {Function} props.onSignUp - Callback when user signs up
 * @param {boolean} props.showInlineSignIn - Whether to show inline sign-in panel
 * @param {Function} props.onCloseInlineSignIn - Callback to close inline sign-in
 * @param {Function} props.onForgotPassword - Callback for forgot password
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ArrowLeft, CheckCircle, Eye, EyeOff, Mail, Sparkles, RefreshCw, BarChart3, Users, ShoppingCart, FileText, Store, Utensils } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';

function PublicLandingPage({ onSignIn, onSignUp, showInlineSignIn, onCloseInlineSignIn, onForgotPassword }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPremiumInterestModal, setShowPremiumInterestModal] = useState(false);
  const [interestEmail, setInterestEmail] = useState('');
  const [isSubmittingInterest, setIsSubmittingInterest] = useState(false);
  const [interestSubmitted, setInterestSubmitted] = useState(false);

  const handleInlineSignIn = async (e) => {
    e.preventDefault();
    if (!login || !password) {
      toast.error('Please enter email/username and password');
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/auth/login`, { login, password });
      
      if (response.data.user && response.data.token) {
        toast.success('Login successful!');
        // The parent component will handle the login
        onSignIn(response.data.user, response.data.token);
      }
    } catch (error) {
      console.error('Login failed:', error);
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handlePremiumInterest = async () => {
    if (!interestEmail || !interestEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmittingInterest(true);
    try {
      const response = await axios.post(`${API}/premium-interest`, {
        email: interestEmail,
        subscribe_to_updates: true
      });

      setInterestSubmitted(true);
      toast.success(response.data.detail || "We'll notify you when Premium launches!");
      
      // Close modal after 2 seconds
      setTimeout(() => {
        setShowPremiumInterestModal(false);
        setInterestEmail('');
      }, 2000);
    } catch (error) {
      console.error('Failed to register interest:', error);
      if (error.response?.data?.detail) {
        if (error.response.data.message === 'already_registered') {
          setInterestSubmitted(true);
          toast.info(error.response.data.detail);
          setTimeout(() => {
            setShowPremiumInterestModal(false);
            setInterestEmail('');
          }, 2000);
        } else {
          toast.error(error.response.data.detail);
        }
      } else {
        toast.error('Failed to register your interest. Please try again.');
      }
    } finally {
      setIsSubmittingInterest(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-red-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_bawarchi-catering-2/artifacts/6e6yv8t6_caterlog-high-resolution-logo-transparent.png" 
              alt="CaterLog" 
              className="h-16 sm:h-20"
              style={{maxWidth: '260px'}}
            />
            {!showInlineSignIn && (
              <div className="flex gap-2 sm:gap-3">
                <button 
                  onClick={() => {
                    // Scroll to pricing section
                    const pricingSection = document.getElementById('pricing-section');
                    if (pricingSection) {
                      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }} 
                  className="btn-bawarchi-primary text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3" 
                  style={{minHeight: '44px'}}
                >
                  View Plans
                </button>
                <div className="relative">
                  <button
                    id="public-contact-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      const dropdown = document.getElementById('public-contact-dropdown');
                      if (!dropdown) return;
                      
                      const isHidden = dropdown.classList.contains('hidden');
                      
                      if (isHidden) {
                        // Opening dropdown
                        dropdown.classList.remove('hidden');
                        
                        // Add click outside listener
                        setTimeout(() => {
                          const closeDropdown = (event) => {
                            const button = document.getElementById('public-contact-button');
                            const dropdown = document.getElementById('public-contact-dropdown');
                            
                            if (dropdown && button && 
                                !dropdown.contains(event.target) && 
                                !button.contains(event.target)) {
                              dropdown.classList.add('hidden');
                              document.removeEventListener('click', closeDropdown);
                            }
                          };
                          document.addEventListener('click', closeDropdown);
                        }, 0);
                      } else {
                        // Closing dropdown
                        dropdown.classList.add('hidden');
                      }
                    }}
                    className="btn-bawarchi-primary text-sm sm:text-base px-4 sm:px-6 py-2 sm:py-3 flex items-center gap-2"
                    style={{minHeight: '44px'}}
                  >
                    <Mail className="w-4 h-4" />
                    Contact Us
                  </button>
                  <div
                    id="public-contact-dropdown"
                    className="hidden absolute top-full mt-2 right-0 bg-white rounded-lg shadow-xl border-2 border-orange-500 p-4 w-64 z-50"
                  >
                    <h3 className="font-bold text-gray-800 mb-3">Contact Us</h3>
                    <div className="space-y-3">
                      <a 
                        href="tel:469-232-7678"
                        className="flex items-center gap-2 text-gray-700 hover:text-orange-600 transition-colors"
                      >
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                        <span className="text-sm font-medium">469-232-7678</span>
                      </a>
                      <a 
                        href="mailto:admin@caterlog.net"
                        className="flex items-center gap-2 text-gray-700 hover:text-orange-600 transition-colors"
                      >
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span className="text-sm font-medium">admin@caterlog.net</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hero Section with Inline Sign-In */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          {/* Sign-In Panel (Inline) or Text Content */}
          {showInlineSignIn ? (
            <div className="bg-white rounded-lg shadow-xl p-8 md:p-10">
              <button
                onClick={onCloseInlineSignIn}
                className="text-orange-600 hover:text-orange-700 flex items-center gap-1 mb-6"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Home
              </button>
              
              <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', fontWeight: '600', marginBottom: '16px' }}>
                Welcome Back
              </h2>
              <p className="text-muted mb-6">Sign in to your CaterLog account</p>
              
              <form onSubmit={handleInlineSignIn} className="space-y-4">
                <div>
                  <label className="label-bawarchi">Email or Username</label>
                  <input
                    type="text"
                    className="input-bawarchi"
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                    placeholder="Enter your email or username"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <div>
                  <label className="label-bawarchi">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      className="input-bawarchi pr-12"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      disabled={loading}
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn-bawarchi-primary w-full flex items-center justify-center gap-2"
                  style={{minHeight: '48px'}}
                  disabled={loading}
                >
                  {loading && (
                    <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  )}
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>
                <div className="text-center">
                  <button
                    type="button"
                    className="text-orange-600 hover:text-orange-700 text-sm font-medium"
                    onClick={onForgotPassword}
                  >
                    Forgot password?
                  </button>
                </div>
              </form>
            </div>
          ) : (
            <div>
            <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '40px', fontWeight: '600', lineHeight: '1.2', marginBottom: '24px' }}>
              Smart Catering Management
              <br />
              <span style={{ color: 'var(--bawarchi-primary)' }}>For Your Restaurant</span>
            </h2>
            <p className="text-lg text-muted mb-8">
              Simplify catering orders, menu management, pricing, and customer communication—all from one easy dashboard.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <button onClick={onSignUp} className="btn-bawarchi-primary text-base sm:text-lg px-6 py-3 sm:px-8 sm:py-4" style={{minHeight: '48px'}}>
                Get Started (Free Trial)
              </button>
              <button onClick={() => onSignIn()} className="btn-bawarchi-secondary text-base sm:text-lg px-6 py-3 sm:px-8 sm:py-4" style={{minHeight: '48px'}}>
                Sign In
              </button>
            </div>
          </div>
          )}
          
          {/* Indian Food Image Grid */}
          <div className="grid grid-cols-2 gap-4">
            <img 
              src="https://images.pexels.com/photos/20642812/pexels-photo-20642812.jpeg?auto=compress&cs=tinysrgb&w=600" 
              alt="Biryani" 
              className="w-full h-48 object-cover rounded-lg shadow-lg"
            />
            <img 
              src="https://images.pexels.com/photos/5031949/pexels-photo-5031949.jpeg?auto=compress&cs=tinysrgb&w=600" 
              alt="Samosas" 
              className="w-full h-48 object-cover rounded-lg shadow-lg"
            />
            <img 
              src="https://images.unsplash.com/photo-1710091691780-c7eb0dc50cf8?w=600&q=80" 
              alt="Indian Curry" 
              className="w-full h-48 object-cover rounded-lg shadow-lg"
            />
            <img 
              src="https://images.unsplash.com/photo-1603894584373-5ac82b2ae398?w=600&q=80" 
              alt="Traditional Curry" 
              className="w-full h-48 object-cover rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', fontWeight: '600', marginBottom: '16px' }}>Everything You Need to Run Your Catering Business</h3>
            <p className="text-muted">Powerful features designed for catering professionals</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-orange-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Store className="w-6 h-6 text-orange-600" />
                </div>
                <h4 className="text-xl font-bold mb-2">Custom Menu & Pricing</h4>
                <p className="text-gray-600">Build your catering menu with flexible tray sizes and per-piece pricing. Import from Excel or create manually.</p>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <ShoppingCart className="w-6 h-6 text-orange-600" />
                </div>
                <h4 className="text-xl font-bold mb-2">Complete Order Management</h4>
                <p className="text-gray-600">Multi-day events, meal selection, pricing, invoices, and more. Everything in one place.</p>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
                <h4 className="text-xl font-bold mb-2">Customer Follow-Ups</h4>
                <p className="text-gray-600">Track anniversaries, send promo emails, and never miss an opportunity. Premium feature.</p>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6 text-orange-600" />
                </div>
                <h4 className="text-xl font-bold mb-2">Reports & Analytics</h4>
                <p className="text-gray-600">12-month rolling sales, KPIs, order trends, and insights to grow your business.</p>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <FileText className="w-6 h-6 text-orange-600" />
                </div>
                <h4 className="text-xl font-bold mb-2">Professional Invoices</h4>
                <p className="text-gray-600">Generate customer and kitchen invoices with your branding. PDF download included.</p>
              </CardContent>
            </Card>
            
            <Card className="border-orange-200 hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                  <Utensils className="w-6 h-6 text-orange-600" />
                </div>
                <h4 className="text-xl font-bold mb-2">Menu Management</h4>
                <p className="text-gray-600">Excel import, pricing tiers, categories, and full menu customization per store.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing-section" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', fontWeight: '600', marginBottom: '16px' }}>Simple, Transparent Pricing</h3>
            <p className="text-muted">Choose the plan that fits your business</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Free Plan */}
            <Card className="border-2 border-gray-200">
              <CardContent className="p-8">
                <h4 className="text-2xl font-bold mb-2">Free Trial</h4>
                <div className="mb-6">
                  <span className="text-4xl font-bold">$0</span>
                  <span className="text-gray-600">/14 days</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>Order creation & management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>Menu management</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>Basic reports</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>Invoice generation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>Excel import/export</span>
                  </li>
                </ul>
                <button onClick={() => onSignUp(null)} className="btn-bawarchi-secondary w-full">Start Free Trial</button>
              </CardContent>
            </Card>

            {/* Base Plan */}
            <Card className="border-2 border-blue-500">
              <CardContent className="p-8">
                <div className="bg-blue-500 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-4">
                  MOST POPULAR
                </div>
                <h4 className="text-2xl font-bold mb-2">Base</h4>
                <div className="mb-6">
                  <span className="text-4xl font-bold">$19.99</span>
                  <span className="text-gray-600">/month</span>
                  <div className="text-sm text-gray-600 mt-1">or $220/year (save $20)</div>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>All core features</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>Multi-user/team support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>Basic reports</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>Customer database</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2 mt-0.5" />
                    <span>Excel import/export</span>
                  </li>
                </ul>
                <button onClick={() => onSignUp('base_monthly')} className="btn-bawarchi-primary w-full">Get Started</button>
              </CardContent>
            </Card>

            {/* Premium Plan - Coming Soon */}
            <Card className="border-2 border-purple-500 bg-gradient-to-br from-purple-50 to-white">
              <CardContent className="p-8">
                <div className="bg-purple-500 text-white text-sm font-bold px-3 py-1 rounded-full inline-block mb-4">
                  COMING SOON
                </div>
                <h4 className="text-2xl font-bold mb-2 flex items-center gap-2">
                  Premium
                  <Sparkles className="w-5 h-5 text-purple-500" />
                </h4>
                <div className="mb-6">
                  <span className="text-2xl font-bold text-purple-600">Coming Early 2026</span>
                </div>
                <div className="mb-6">
                  <p className="text-sm text-gray-600 mb-3">
                    We're building something special! Premium will include:
                  </p>
                </div>
                <ul className="space-y-2 mb-8">
                  <li className="flex items-start">
                    <Sparkles className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">Everything in Base</span>
                  </li>
                  <li className="flex items-start">
                    <Sparkles className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm"><strong>Advanced Analytics & Reports</strong></span>
                  </li>
                  <li className="flex items-start">
                    <Sparkles className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm"><strong>Customer Segmentation</strong></span>
                  </li>
                  <li className="flex items-start">
                    <Sparkles className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm"><strong>Customer Ratings & Reviews</strong></span>
                  </li>
                  <li className="flex items-start">
                    <Sparkles className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm"><strong>AI-Powered Menu Optimization</strong></span>
                  </li>
                  <li className="flex items-start">
                    <Sparkles className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm"><strong>Multi-Store Management</strong></span>
                  </li>
                  <li className="flex items-start">
                    <Sparkles className="w-4 h-4 text-purple-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm"><strong>Priority Support</strong></span>
                  </li>
                </ul>
                <button 
                  onClick={() => setShowPremiumInterestModal(true)} 
                  className="btn-bawarchi-primary w-full flex items-center justify-center gap-2" 
                  style={{backgroundColor: '#9333ea'}}
                >
                  <Mail className="w-4 h-4" />
                  Notify Me
                </button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Footer CTA */}
      <div style={{ backgroundColor: 'var(--bawarchi-primary)' }} className="text-white py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h3 style={{ fontFamily: 'Playfair Display, serif', fontSize: '32px', fontWeight: '600', marginBottom: '16px' }}>Ready to Transform Your Catering Business?</h3>
          <p className="text-xl mb-8" style={{ color: 'rgba(255,255,255,0.9)' }}>Start your 14-day free trial today. No credit card required.</p>
          <button onClick={onSignUp} className="btn-bawarchi-primary text-lg px-8 py-6" style={{backgroundColor: 'white', color: 'var(--bawarchi-primary)'}}>
            Get Started Free
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-gray-400 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p>&copy; 2025 CaterLog. All rights reserved.</p>
        </div>
      </div>

      {/* Premium Interest Modal */}
      <Dialog open={showPremiumInterestModal} onOpenChange={setShowPremiumInterestModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Get Notified About Premium
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Be the first to know when Premium launches in Early 2026! Enter your email below.
            </p>
            {!interestSubmitted ? (
              <>
                <div>
                  <Label htmlFor="interest-email">Email Address</Label>
                  <Input
                    id="interest-email"
                    type="email"
                    placeholder="you@example.com"
                    value={interestEmail}
                    onChange={(e) => setInterestEmail(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handlePremiumInterest();
                      }
                    }}
                  />
                </div>
                <button
                  onClick={handlePremiumInterest}
                  disabled={isSubmittingInterest}
                  className="btn-bawarchi-primary w-full flex items-center justify-center gap-2"
                >
                  {isSubmittingInterest ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      Notify Me
                    </>
                  )}
                </button>
              </>
            ) : (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-lg font-semibold text-green-700">You're on the list!</p>
                <p className="text-sm text-gray-600 mt-2">We'll notify you when Premium launches.</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Sign Up Page Component

// Store Setup Page Component (Store Admin) - Phase 2 & 3

// Forgot Password Page Component

// Reset Password Page Component

// Forced Password Change Modal (cannot be dismissed)
// Welcome Hint Modal for First-Time Users

export default PublicLandingPage;
